import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { LeadService } from '../services/lead.service';
import { SnackBarService } from '../services/snack-bar.service';
import { environment } from '../../environments/environment';

interface DebtInfo {
  bankName: string;
  balance: number | string;
}

interface MortgageInfo {
  type: string;
  value: number | string | null;
}

interface VehicleInfo {
  model: string;
  year: number;
  finance: number;
}

interface BusinessInfo {
  name: string;
  hst: string;
  articleOfIncorporation: string;
  debt: number;
}

interface FirstMeetingData {
  spouse_income: string;
  child_tax: string;
  rental_income: string;
  family_support: string;
  any_other_income: string;
  debtors_income: string;
  id?: string;
  clientName?: string;
  phoneNumber?: string;
  emailId?: string;
  debtInfo?: DebtInfo[];
  assets?: {
    firstMortgage?: MortgageInfo[];
    secondMortgage?: MortgageInfo[];
    propertyValue?: number;
    lopNeeded?: string;
  };
  vehicleInfo?: VehicleInfo[];
  businessInfo?: BusinessInfo;
  note?: string;
  terms?: string;
  // API response fields
  client_name?: string;
  phone_number?: string;
  email?: string;
  property_value?: number;
  lop_needed?: number;
  vehicle_model?: string;
  vehicle_year?: number;
  vehicle_finance_amount?: number;
  bsns_name?: string;
  bsns_hst?: string;
  bsns_article_ins?: string;
  bsns_deal?: number;
  notes?: string;
  debt_info?: Array<{ name: string; balance: number }>;
  assets_info?: Array<{ mortgage: number }>;
}

@Component({
  selector: 'app-firstmeeting-edit',
  templateUrl: './firstmeeting-edit.component.html',
  styleUrls: ['./firstmeeting-edit.component.css']
})
export class FirstmeetingEditComponent implements OnInit {
  form: FormGroup;
  debtList: DebtInfo[] = [];
  mortgageList: MortgageInfo[] = [];
  isSubmitting = false;
  isLoading = false;
  meetingId: string = '';
  meetingData: FirstMeetingData | null = null;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private leadService: LeadService,
    private snackBarService: SnackBarService
  ) {
    this.form = this.fb.group({
      clientName: ['', [Validators.required, Validators.minLength(2)]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      emailId: ['', [Validators.required, Validators.email]],
      debtorIncome: [''],
      spouseIncome: [''],
      childTax: [''],
      rentalIncome: [''],
      familySupport: [''],
      otherIncome: [''],
      propertyValue: [''],
      lopNeeded: ['no'],
      vehicleModel: [''],
      vehicleYear: [''],
      vehicleFinance: [''],
      businessName: [''],
      businessHst: [''],
      businessArticle: [''],
      businessDebt: [''],
      note: [''],
      terms: ['']
    });
  }

  ngOnInit(): void {
    this.meetingId = this.route.snapshot.paramMap.get('id') || '';
    if (this.meetingId) {
      this.loadMeetingData();
    } else {
      // Load static data for new meeting
    }
  }

  loadMeetingData(): void {
    this.isLoading = true;
    const apiUrl = `${environment.BASE_URL}/first-meeting/view/${this.meetingId}`;
    
    this.http.get<any>(apiUrl).subscribe({
      next: (response) => {
        this.meetingData = response.data || response;
        this.populateForm();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading meeting data:', error);
        this.isLoading = false;
        // Load static data as fallback
      }
    });
  }

  populateForm(): void {
    if (!this.meetingData) return;

    // Map API response to form structure
    const data = this.meetingData;
    
    this.form.patchValue({
      clientName: data.client_name || data.clientName || '',
      phoneNumber: data.phone_number || data.phoneNumber || '',
      emailId: data.email || data.emailId || '',
      debtorIncome: data.debtors_income || '',
      spouseIncome: data.spouse_income || '',
      childTax: data.child_tax || '',
      rentalIncome: data.rental_income || '',
      familySupport: data.family_support || '',
      otherIncome: data.any_other_income || '',
      propertyValue: data.property_value || data.assets?.propertyValue || '',
      lopNeeded: (data.lop_needed === 1 || data.assets?.lopNeeded === 'yes') ? 'yes' : 'no',
      vehicleModel: data.vehicle_model || (data.vehicleInfo?.[0]?.model) || '',
      vehicleYear: data.vehicle_year || (data.vehicleInfo?.[0]?.year) || '',
      vehicleFinance: data.vehicle_finance_amount || (data.vehicleInfo?.[0]?.finance) || '',
      businessName: data.bsns_name || data.businessInfo?.name || '',
      businessHst: data.bsns_hst || data.businessInfo?.hst || '',
      businessArticle: data.bsns_article_ins || data.businessInfo?.articleOfIncorporation || '',
      businessDebt: data.bsns_deal || data.businessInfo?.debt || '',
      note: data.notes || data.note || '',
      terms: data.terms || ''
    });

    // Populate debt list
    if (data.debt_info && Array.isArray(data.debt_info)) {
      this.debtList = data.debt_info.map(d => {
        const balance = typeof d.balance === 'string' ? parseFloat(d.balance) : d.balance;
        return {
          bankName: d.name || '',
          balance: isNaN(balance) ? 0 : balance
        };
      });
    } else if (data.debtInfo) {
      this.debtList = [...data.debtInfo];
    }

    // Populate mortgage list
    if (data.assets_info && Array.isArray(data.assets_info) && data.assets_info.length > 0) {
      this.mortgageList = data.assets_info.map(m => {
        let mortgageValue: number | string | null = null;
        if (m.mortgage) {
          const parsed = parseFloat(String(m.mortgage));
          // If it's a valid number, use the parsed value; otherwise keep the original string
          mortgageValue = !isNaN(parsed) ? parsed : String(m.mortgage);
        }
        return {
          type: 'mortgage',
          value: mortgageValue
        };
      });
    } else if (data.assets) {
      const allMortgages = [...(data.assets.firstMortgage || []), ...(data.assets.secondMortgage || [])];
      this.mortgageList = allMortgages.map(m => ({ type: 'mortgage', value: m.value }));
    }
  }

  addDebt(): void {
    this.debtList.push({ bankName: '', balance: null as any });
  }

  removeDebt(index: number): void {
    this.debtList.splice(index, 1);
  }

  addMortgage(): void {
    this.mortgageList.push({ type: 'mortgage', value: null as any });
  }

  removeMortgage(index: number): void {
    this.mortgageList.splice(index, 1);
  }

getMortgageLabel(index: number): string {
  const ordinals: string[] = [
    "First", "Second", "Third", "Fourth", "Fifth",
    "Sixth", "Seventh", "Eighth", "Ninth", "Tenth",
    "Eleventh", "Twelfth", "Thirteenth", "Fourteenth", "Fifteenth",
    "Sixteenth", "Seventeenth", "Eighteenth", "Nineteenth"
  ];

  const tens: string[] = [
    "", "", "Twentieth", "Thirtieth", "Fortieth",
    "Fiftieth", "Sixtieth", "Seventieth", "Eightieth", "Ninetieth"
  ];

  const ones: string[] = [
    "", "First", "Second", "Third", "Fourth", "Fifth",
    "Sixth", "Seventh", "Eighth", "Ninth"
  ];

  const num = index + 1;

  if (num <= 19) {
    return `${ordinals[num - 1]} Mortgage`;
  }

  if (num < 100) {
    const ten = Math.floor(num / 10);
    const one = num % 10;

    if (one === 0) {
      return `${tens[ten]} Mortgage`;
    }

    return `${tens[ten].replace("ieth", "y")} ${ones[one]} Mortgage`;
  }

  if (num === 100) {
    return `Hundredth Mortgage`;
  }

  return `Mortgage ${num}`;
}


  onSubmit(): void {
    const clientName = this.form.get('clientName')?.value?.trim();
    const phoneNumber = this.form.get('phoneNumber')?.value?.trim();
    const email = this.form.get('emailId')?.value?.trim();

    if (!clientName || !phoneNumber || !email) {
      this.snackBarService.openSnackBar('Please fill in all mandatory fields: Name, Phone, and Email', 'error-class');
      return;
    }

    if (!this.form.valid) {
      this.snackBarService.openSnackBar('Please fix the errors in the form', 'error-class');
      return;
    }

    const payload = {
      customer_id: parseInt(this.meetingId),
      client_name: clientName,
      phone_number: phoneNumber,
      email: email,
      property_value: this.form.get('propertyValue')?.value || '',
      lop_needed: this.form.get('lopNeeded')?.value === 'yes' ? 1 : 0,
      vehicle_model: this.form.get('vehicleModel')?.value || '',
      vehicle_year: this.form.get('vehicleYear')?.value || '',
      vehicle_finance_amount: this.form.get('vehicleFinance')?.value || '',
      bsns_name: this.form.get('businessName')?.value || '',
      bsns_hst: this.form.get('businessHst')?.value || '',
      bsns_article_ins: this.form.get('businessArticle')?.value || '',
      bsns_deal: this.form.get('businessDebt')?.value || '',
      notes: this.form.get('note')?.value || '',
      terms: this.form.get('terms')?.value || '',
      debtors_income: this.form.get('debtorIncome')?.value || '',
      spouse_income: this.form.get('spouseIncome')?.value || '',
      child_tax: this.form.get('childTax')?.value || '',
      rental_income: this.form.get('rentalIncome')?.value || '',
      family_support: this.form.get('familySupport')?.value || '',
      any_other_income: this.form.get('otherIncome')?.value || '',
      assets_info: this.mortgageList.map(m => m.value ? { mortgage: m.value } : null).filter(m => m !== null),
      debt_info: this.debtList.map(d => ({ name: d.bankName, balance: d.balance }))
    };

    this.isSubmitting = true;
    const apiUrl = `${environment.BASE_URL}/first-meeting/save/${this.meetingId}`;
    
    this.http.post<any>(apiUrl, payload).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.snackBarService.openSnackBar('First meeting saved successfully', 'success-class');
        this.router.navigate(['/dashboard/customer-list']);
      },
      error: (error) => {
        this.isSubmitting = false;
        console.error('Error saving first meeting:', error);
        this.snackBarService.openSnackBar(
          error.error?.message || 'Error saving first meeting',
          'error-class'
        );
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/dashboard/customer-list']);
  }

  validateAllSections(): boolean {
    return true;
  }

  getPhoneNumberError(): string {
    const control = this.form.get('phoneNumber');
    if (control?.hasError('required')) {
      return 'Phone number is required';
    }
    if (control?.hasError('pattern')) {
      return 'Phone number must be 10 digits';
    }
    return '';
  }

  getEmailError(): string {
    const control = this.form.get('emailId');
    if (control?.hasError('required')) {
      return 'Email is required';
    }
    if (control?.hasError('email')) {
      return 'Invalid email format';
    }
    return '';
  }
}
